import isNumber from 'lodash/isNumber';
import React, {useState} from 'react';
import type {WithRouterProps} from 'react-router';
import {withRouter} from 'react-router';

import {Button, Icon, LoadingSpinner, Text} from '@albert-io/atomic';
import MarkdownRendererV2 from 'generic/MarkdownRendererV2/MarkdownRendererV2.react';
import {calculateDueDate} from 'lib/timeUtils';
import {DueDateBadge} from './DueDateBadge';
import {ProficiencyChartBadges} from 'client/Reports/components/Table/Columns/ProficiencyChart/ProficiencyChart';
import type {DiagnosticItem, SkillSubjectGroup} from './types';
import TabEmptyState from '../TabEmptyState';
import {SkillPathListGroup} from 'client/TeacherDashboard/SkillPaths/SkillPathListGroup';

const DIAGNOSTICS_SHOWN_BY_DEFAULT = 3;

/** Long lists are collapsed so the skill practice below stays reachable without scrolling past them. */
const DiagnosticList = ({diagnostics}: {diagnostics: DiagnosticItem[]}) => {
  'use memo';

  const [showAll, setShowAll] = useState(false);
  const hiddenCount = diagnostics.length - DIAGNOSTICS_SHOWN_BY_DEFAULT;
  const visible = showAll ? diagnostics : diagnostics.slice(0, DIAGNOSTICS_SHOWN_BY_DEFAULT);

  return (
    <div className='gap-x1 flex flex-col items-start'>
      <div className='-mx-x1 flex w-full flex-col'>
        {visible.map((diagnostic) => (
          <DiagnosticPathItem key={diagnostic.diagnosticId} diagnostic={diagnostic} />
        ))}
      </div>
      {hiddenCount > 0 && !showAll && (
        <Button
          color='secondary'
          variant='outlined'
          size='s'
          roundness='pill'
          // Indented to sit under the rows' content, level with Browse all skills below.
          className='m:ml-x7'
          onClick={() => setShowAll(true)}
        >
          <Text size='m' color='inherit' className='font-normal'>
            {`Show ${hiddenCount} More`}
          </Text>
        </Button>
      )}
    </div>
  );
};

interface StudentSkillPathListProps {
  skillSubjectGroups: SkillSubjectGroup[];
  diagnostics: DiagnosticItem[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

interface SkillPathItemProps extends WithRouterProps<{}> {
  guideLevelId: string;
  name: string;
  code?: string | null;
  dueDate?: string | null;
  myProficiencyLevel?: number | null;
}

const SkillPathItemInner = ({
  router,
  guideLevelId,
  name,
  code,
  dueDate,
  myProficiencyLevel
}: SkillPathItemProps) => {
  // React Compiler opt-out: calculateDueDate() derives from current time (moment() +
  // systemTimeOffsetStore) in render, so compiling would memoize dueDateInfo as
  // dependency-free and freeze it for the session. Same class as Assignment.tsx.
  // A plain comment isn't enough here: other components in this file carry 'use
  // memo', which is enough for the compiler to compile this function too — the
  // explicit 'use no memo' below is required to actually exclude it.
  'use no memo';

  const handleClick = () => {
    router.push(`/adaptive/skill/${guideLevelId}`);
  };

  const dueDateInfo = dueDate ? calculateDueDate(dueDate) : null;

  return (
    <div
      className='gap-x0-half p-x0-half hover:background-subtle flex cursor-pointer flex-wrap items-center justify-between'
      role='button'
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className='gap-x1 flex flex-wrap items-baseline'>
        <Text as={MarkdownRendererV2} text={name} size='m' color='primary' />
        <Text size='m' color='tertiary'>
          ({code})
        </Text>
      </div>
      <div className='gap-x1-half flex items-center'>
        <div className='min-w-fit'>{dueDateInfo && <DueDateBadge dueDateInfo={dueDateInfo} />}</div>
        <ProficiencyChartBadges
          proficiencyLevel={myProficiencyLevel}
          showNotStarted={false}
          showLabel={false}
          badgeSize={16}
          iconGap={0}
          className='proficiency-chart__student_browsing [&_.empty-badge-component]:fill-background-bold!'
          withTooltip={isNumber(myProficiencyLevel)}
        />
      </div>
    </div>
  );
};

const SkillPathItem = withRouter(SkillPathItemInner);

interface DiagnosticPathItemProps extends WithRouterProps<{}> {
  diagnostic: DiagnosticItem;
}

const DiagnosticPathItemInner = ({router, diagnostic}: DiagnosticPathItemProps) => {
  // React Compiler opt-out: calculateDueDate() derives from current time (moment() +
  // systemTimeOffsetStore) in render, so compiling would memoize dueDateInfo as
  // dependency-free and freeze it for the session. Same class as Assignment.tsx.
  // A plain comment isn't enough here: other components in this file carry 'use
  // memo', which is enough for the compiler to compile this function too — the
  // explicit 'use no memo' below is required to actually exclude it.
  'use no memo';

  const handleClick = () => {
    router.push(`/diagnostics/${diagnostic.diagnosticId}`);
  };

  const dueDateInfo = diagnostic.dueDate ? calculateDueDate(diagnostic.dueDate) : null;

  return (
    <div
      className='gap-x2 rounded-x1 p-x1 hover:background-subtle flex cursor-pointer flex-wrap items-center justify-between'
      role='button'
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
    >
      <div className='gap-x2 flex items-center'>
        <div className='h-x5 w-x5 background-special-subtle flex shrink-0 items-center justify-center rounded-full'>
          <Icon icon={['fal', 'file-contract']} className='content-special!' />
        </div>
        <div className='flex flex-col'>
          <Text size='m' color='primary'>
            {diagnostic.name}
          </Text>
          <Text size='s' color='tertiary'>
            {`${diagnostic.guideName} • ${diagnostic.subjectName}`}
          </Text>
        </div>
      </div>
      <div className='min-w-fit'>{dueDateInfo && <DueDateBadge dueDateInfo={dueDateInfo} />}</div>
    </div>
  );
};

const DiagnosticPathItem = withRouter(DiagnosticPathItemInner);

interface BrowseAllSkillsLinkProps extends WithRouterProps<{}> {
  href: string;
}

const BrowseAllSkillsLinkInner = ({router, href}: BrowseAllSkillsLinkProps) => {
  'use memo';

  return (
    <Button
      color='secondary'
      variant='outlined'
      size='s'
      onClick={() => router.push(href)}
      roundness='pill'
      className='w-[149px]'
    >
      <Text size='m' color='inherit' className='font-normal'>
        Browse all skills
      </Text>
      <Icon className='ml-x1-quarter' icon={['far', 'arrow-up-right']} />
    </Button>
  );
};

const BrowseAllSkillsLink = withRouter(BrowseAllSkillsLinkInner);

export function SkillPathList({
  skillSubjectGroups,
  diagnostics,
  isLoading,
  isError,
  onRetry
}: StudentSkillPathListProps) {
  'use memo';

  if (isLoading) {
    return (
      <div className='p-x4 flex items-center justify-center'>
        <LoadingSpinner size={3} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='gap-x4 border-t-border-subtle p-x2 m:p-x4 flex flex-col border-t'>
        <TabEmptyState
          copy="We couldn't load your skill path."
          subtitle='Something went wrong. Check your connection and try again.'
          icon={['fal', 'circle-exclamation']}
          onRetry={onRetry}
        />
      </div>
    );
  }

  if (skillSubjectGroups.length === 0 && diagnostics.length === 0) {
    return (
      <div className='gap-x4 border-t-border-subtle p-x2 m:p-x4 flex flex-col border-t'>
        <TabEmptyState
          copy='No diagnostics or skill paths to practice.'
          icon={['fal', 'sparkles']}
        />
      </div>
    );
  }

  const sortedSubjectGroups = [...skillSubjectGroups].sort((a, b) => {
    const aCount = a.skillGuides.reduce((sum, s) => sum + s.items.length, 0);
    const bCount = b.skillGuides.reduce((sum, s) => sum + s.items.length, 0);
    return bCount - aCount;
  });

  return (
    <div className='gap-x4 border-t-border-subtle p-x2 m:p-x4 flex flex-col border-t'>
      <SkillPathListGroup title='Diagnostics'>
        {diagnostics.length > 0 ? (
          <DiagnosticList diagnostics={diagnostics} />
        ) : (
          /* No icon by design, so the diagnostics area stays compact above the skills. */
          <TabEmptyState copy='No diagnostics to take' />
        )}
      </SkillPathListGroup>

      <SkillPathListGroup title='Skill Practice'>
        {sortedSubjectGroups.length === 0 && (
          <TabEmptyState copy='No skill paths to practice' icon={['fal', 'sparkles']} />
        )}
        {sortedSubjectGroups.map((group) => (
          <div key={group.subjectId} className='gap-x2 flex flex-col'>
            <div className='gap-x2 flex items-center'>
              <div className='h-x5 w-x5 background-special-subtle flex items-center justify-center rounded-full'>
                <Icon icon={['fal', 'sparkles']} className='content-special!' />
              </div>
              <div className='flex flex-col'>
                <Text size='l' color='primary'>
                  {group.subjectName}
                </Text>
              </div>
            </div>

            <div className='gap-x0-half m:pl-x7 flex flex-col'>
              {group.skillGuides.map((guide) => (
                <div key={guide.guideId} className='mb-x1 flex flex-col'>
                  {guide.guideName && guide.guideName.toLowerCase() !== 'adaptive' && (
                    <div className='p-x0-half'>
                      <Text
                        as={MarkdownRendererV2}
                        text={guide.guideName}
                        size='s'
                        color='primary'
                        bold
                      />
                    </div>
                  )}
                  <div className='gap-x0-half flex flex-col'>
                    {guide.items.map((item) => (
                      <SkillPathItem
                        key={item.id}
                        guideLevelId={item.id}
                        name={item.name}
                        code={item.code}
                        dueDate={item.dueDate}
                        myProficiencyLevel={item.myProficiencyLevel}
                      />
                    ))}
                  </div>
                </div>
              ))}
              <BrowseAllSkillsLink href={group.browseUrl} />
            </div>
          </div>
        ))}
      </SkillPathListGroup>
    </div>
  );
}
